import * as i18next from 'i18next';
import { t } from 'i18next';

import { FileFormats } from '../constants/FileFormat';
import { ViewSettingsActions } from '../actions/ViewSettingsActions';
import { DialogActions, UnsupportedFileError, FileImportError } from '../actions/DialogActions';
import { SceneActions } from '../actions/SceneActions';
import { AppDocumentContainer } from './AppDocumentContainer';
import { GlobalHelper } from './GlobalHelper';
import { Stats } from './Stats';
import { PersistentStore } from '../stores/PersistentStore';
import { ScreenshotHelper } from './ScreenshotHelper';
import { ShareImageSize } from '../constants/LayoutConstants';
import { ProjectManagerStore } from '../stores/ProjectManagerStore';
import { ArticleActions } from '../actions/ArticleActions';
import { ArticleStore } from '../stores/ArticleStore';
import { UIHelper, PromiseCallbacks } from './UIHelper';
import { DownloadHelper } from './DownloadHelper';
import { InteractionQueue } from './InteractionQueue';
import { Workbook, XLSXDocument } from './XLSXDocument';
import { PDFMaker } from './PDFMaker';
import { ExportFormat } from '../constants/ExportFormats';

// Submodules
import * as AppFileApiWrapper from '../submodules/app-api/Filesystem/Wrapper';
import { Dict } from '../submodules/utils/Types';
import { sendMessage } from '../submodules/app-api/Communication';
import { DownloadCancled } from '../submodules/app-api/AppAction';
import { share } from '../submodules/app-api/Share';
import { readBinaryFile } from '../submodules/app-api/Filesystem/Helper';

import utils = egr.wcf.utils;

interface WebAction {
    type: string;
    action: string;
    arguments?: any;
}

interface LanguageSetting {
    lng: string;
    res?: any;
}

export abstract class AppHelper {
    public static msgHook: Array<(msg: {[id: string]: any}) => void> = [];

    public static setLanguage(language: string): Promise<any> {
        const fallbackTranslation: {lng: string, res?: any} = {lng: 'en'};
        const allowedLangs: Array<string> = [
            'da', 'de', 'en', 'es', 'fr', 'it', 'nl', 'pl', 'pt', 'ro', 'tr'
        ];
        return new Promise<void>((resolve: () => void, reject: () => void) => {
            if (allowedLangs.indexOf(language) !== -1) {
                egr.wcf.utils.async.ajax<Dict<string>>('GET', 'i18n/' + language + '/translation.json', null, {
                    dataType: 'json'
                }).then(
                    (data: Dict<string>): LanguageSetting => {return {lng: language, res: data}; },
                    (): LanguageSetting => { return fallbackTranslation; }
                ).then(
                    (settings: LanguageSetting): void => {
                        const mapping: any = {};
                        if (settings.res != null) {
                            mapping[settings.lng] = {
                                translation: settings.res
                            };
                        }
                        i18next.init(
                            {
                                lng: settings.lng,
                                resources: mapping,
                                returnEmptyString: false,
                                nsSeparator: '',
                                keySeparator: ''
                            },
                            resolve
                        );
                    },
                    reject
                );
            } else {
                reject();
            }
        });
    }

    public static importFromFilesystem(): void {
        void sendMessage(
            {
                type: 'appaction',
                action: 'import',
                arguments: {
                    file_types: ['com.easterngraphics.pbox', 'com.easterngraphics.pec']
                }
            },
            true
        );
    }

    public static async shareLinkContent(format: ExportFormat): Promise<void> {
        if (['OBX', 'OBK'].indexOf(format) !== -1) {
            const continueExport: boolean = await DialogActions.inconsistencyDialog();
            if (!continueExport) {
                return;
            }
        }

        Stats.sendEvent({
            category: 'Send',
            action: 'app',
            label: format
        });

        return ViewSettingsActions.withLock<void>(
            async (): Promise<void> => {
                try {
                    let uri: string | null = null;
                    try {
                        uri = await DownloadHelper.getExportUrl(format);
                    } catch {
                        uri = null;
                    }

                    if (utils.string.isNullOrEmpty(uri)) {
                        UIHelper.showMessage(t('The CAD file could not be generated.'));
                        throw new DownloadCancled('Download canceled');
                    }

                    return await share({
                        uri,
                        contentType: FileFormats[format].mimetype,
                        filename: ProjectManagerStore.getProjectName(true) + '.' + FileFormats[format].extension
                    });
                } catch (error) {
                    if (!(error instanceof DownloadCancled)) {
                        throw error;
                    }
                }
            }
        );
    }

    public static sharePboxFile(): Promise<void> {
        Stats.sendEvent({
            category: 'Send',
            action: 'app',
            label: 'PBOX'
        });
        Stats.sendEvent({
            action: 'Save'
        });

        return ViewSettingsActions.withLock<void>(async (): Promise<void> => {
            await UIHelper.waitForLockVisibility();
            await ArticleActions.updateArticleList(); // FixMe: update before lock!?
            const data: ArrayBuffer = await GlobalHelper.coreApp.document.saveToZip({
                // Note: the `compressionLevel` is necessary because
                // of the ugly hack in the `ProjectSettingsModule.saveDocumentData` function
                compressionLevel: 4,
                embedGeometries: true,
                embedTextures: true
            });
            return share({
                uri: data,
                contentType: FileFormats.PBOX.mimetype,
                filename: ProjectManagerStore.getProjectName(true) + '.' + FileFormats.PBOX.extension,
            });
        });
    }

    public static shareArticleListPDF(): Promise<void> {
        Stats.sendEvent({
            category: 'Send',
            action: 'app',
            label: 'PDF'
        });

        return ViewSettingsActions.withLock<void>(async (): Promise<void> => {
            await UIHelper.waitForLockVisibility();
            await ArticleActions.updateArticleList(); // FixMe: update before lock!?
            const data: ArrayBuffer = utils.arraybuffer.fromBase64(await PDFMaker.generatePDFAsBase64());
            try {
                return share({
                    uri: data,
                    contentType: FileFormats.PDF.mimetype,
                    filename: t('Article List') + '.' + FileFormats.PDF.extension,
                });
            } catch (error) {
                //
            }
        });
    }

    public static shareArticleListExcel(): Promise<void> {
        Stats.sendEvent({
            category: 'Send',
            action: 'app',
            label: 'EXCEL'
        });

        return ViewSettingsActions.withLock<void>(async (): Promise<void> => {
            await UIHelper.waitForLockVisibility();
            await ArticleActions.updateArticleList(); // FixMe: update before lock!?
            const xlsx: XLSXDocument = new XLSXDocument(ArticleStore.getState().articleList);
            const workbook: Workbook = await xlsx.getDocument(UIHelper.showPrices());
            const data: ArrayBuffer = utils.arraybuffer.fromBase64(await workbook.outputAsync('base64') as string);
            try {
                return share({
                    uri: data,
                    contentType: FileFormats.EXCEL.mimetype,
                    filename: t('Article List') + '.' + FileFormats.EXCEL.extension,
                });
            } catch (error) {
                //
            }
        });
    }

    public static shareScreenshot(silent: boolean = false): Promise<void> {
        if (silent) {
            Stats.sendEvent({
                category: 'Download',
                action: 'download',
                label: PersistentStore.getState().imageFormat
            });
        } else {
            Stats.sendEvent({
                category: 'Send',
                action: 'app',
                label: PersistentStore.getState().imageFormat
            });
        }

        // the timeout should resolve the promise because the animation
        // is a nice to have feature but not important enough to break the
        // action

        return ViewSettingsActions.withLock<void>(AppHelper.triggerScreenshot);
    }

    // debug code: should be removed later on
    public static sendReply(transactionId: number, error: boolean, data?: any): void {
        window.postMessage(
            {
                transaction_id: transactionId,
                type: 'reply',
                error: error,
                data: data
            },
            '*'
        );
    }

    public static async openFile(event: MessageEvent): Promise<void> {
        try {
            await InteractionQueue.add((): Promise<void> => {
                return AppHelper.handleOpenFileAction(event.data);
            });
        } catch (exception) {
            if (
                exception instanceof FileImportError ||
                exception instanceof UnsupportedFileError
            ) {
                UIHelper.errorDialog((exception as Error).message);
            } else if (exception != null && utils.string.isNotNullOrEmpty((exception as Error).message)) {
                UIHelper.errorDialog((exception as Error).message);
            } else {
                UIHelper.errorDialog(t('Could not open file'));
            }
        } finally {
            await UIHelper.ignoreReject(AppFileApiWrapper.remove(event.data.arguments.path));
        }
    }

    private static async triggerScreenshot(): Promise<void> {
        let content: string | undefined;

        content = await ScreenshotHelper.getScreenshot({
            width: ShareImageSize.width,
            height: ShareImageSize.height,
            renderAsync: true,
            addWatermark: true,
            customMimetype: true
        });

        if (utils.string.isNotNullOrEmpty(content)) {
            return share({
                uri: content,
                contentType: PersistentStore.getState().imageFormat === 'JPG' ? FileFormats.PNG.mimetype : FileFormats.JPG.mimetype,
                filename: PersistentStore.getState().imageFormat === 'JPG' ? 'screenshot.png' : 'screenshot.jpg'
            });
        }
    }

    /**
     * Can throw FileImportError or UnsupportedFileError
     * @param data
     */
    private static async handleOpenFileAction(data: WebAction): Promise<void> {
        let fileType: string = data.arguments.type;
        if (utils.string.isNullOrEmpty(fileType) && !utils.string.isNullOrEmpty(data.arguments.filename)) {
            fileType = data.arguments.filename.split('.').pop();
        }

        if (data.arguments.error === true) {
            throw new FileImportError(t('Could not read the file'));
        }
        if (utils.string.isNotNullOrEmpty(fileType)) {
            switch (fileType.toLocaleLowerCase()) {
                case 'obk':
                    const obkContent: ArrayBuffer = await readBinaryFile(data.arguments.path);
                    return DialogActions.openProjectFromFile({
                        type: 'OBK',
                        content: new Blob([obkContent])
                    });
                case 'pbox':
                    return DialogActions.openProjectFromFile({
                        type: 'PBOX',
                        content: new AppDocumentContainer(data.arguments.path)
                    });
                case 'pec':
                    const content: ArrayBuffer = await ViewSettingsActions.withLock<ArrayBuffer>(() => {
                        return readBinaryFile(data.arguments.path);
                    });

                    type ActionType = 'import' | 'open' | 'cancel';
                    let action: ActionType = 'open';
                    if (ProjectManagerStore.getCurrentProject() != null) {
                        const desicionPromise: PromiseCallbacks<ActionType> = UIHelper.getPromiseCallbacks<ActionType>();
                        DialogActions.showImportDialog({
                            importCallback: (): void => {
                                desicionPromise.resolve('import');
                            },
                            newSceneCallback: (): void => {
                                desicionPromise.resolve('open');
                            },
                            cancelCallback: (): void => {
                                desicionPromise.resolve('cancel');
                            }
                        });
                        action = await desicionPromise.promise;
                    }

                    switch (action) {
                        case 'open':
                            await DialogActions.openProjectFromFile({
                                type: 'PEC',
                                content: content
                            });
                            break;
                        case 'import':
                            Stats.sendEvent({
                                action: 'Plandat'
                            });
                            await SceneActions.importPecFile(content);
                            break;
                        default:
                            return;
                    }
                    break;
                default:
                    throw new UnsupportedFileError(t('Unsupported file type'));
            }
        }
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/AppHelper.ts